import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Combobox } from '../../components/ui/Combobox';
import { Input } from '../../components/ui/input';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { ApiError } from '../../lib/api';
import { MAX_CLUBS_PER_POOL } from '../../types/speedToLead';
import { listAccounts } from '../accounts';
import { createPool, deletePool, fetchPools, updatePool, type PoolBody } from './api';

// THE NUMBER POOL EDITOR (moved, unchanged, from the old Settings -> Speed to Lead page; KyckConnect -> Numbers and pools hosts it).
// A pool is up to MAX_CLUBS_PER_POOL clubs of one brand sharing Vonage numbers; a dedicated number is a pool of one club. Writes
// are admin only (the server refuses anyone else).

function errMessage(err: unknown): string {
  return err instanceof ApiError || err instanceof Error ? err.message : 'Something went wrong.';
}

function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-md border border-slate-200 bg-white p-6">
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      {description && <p className="mb-4 mt-0.5 text-sm text-slate-500">{description}</p>}
      {children}
    </section>
  );
}

type PoolForm = { id: string | null; name: string; brandAccountId: string; clubAccountIds: string[]; numbers: string[]; groupStaff: Array<{ name: string; mobile: string }>; primaryNumber: string };
const EMPTY_POOL: PoolForm = { id: null, name: '', brandAccountId: '', clubAccountIds: [], numbers: [], groupStaff: [], primaryNumber: '' };

export function PoolsEditor({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const pools = useQuery({ queryKey: ['speed-to-lead', 'pools'], queryFn: fetchPools });
  const accounts = useQuery({ queryKey: ['accounts', 'all'], queryFn: listAccounts, staleTime: 5 * 60_000 });
  const [form, setForm] = useState<PoolForm | null>(null);

  const brands = useMemo(() => (accounts.data ?? []).filter((a) => a.accountType === 'brand').map((a) => ({ value: a.id, label: a.name })), [accounts.data]);
  const nameOf = useMemo(() => new Map((accounts.data ?? []).map((a) => [a.id, a.name])), [accounts.data]);
  const clubOptions = useMemo(
    () => (accounts.data ?? []).filter((a) => (a.accountType === 'location' || a.accountType === 'dealership') && !form?.clubAccountIds.includes(a.id)).map((a) => ({ value: a.id, label: a.name })),
    [accounts.data, form?.clubAccountIds],
  );

  const done = () => {
    setForm(null);
    void qc.invalidateQueries({ queryKey: ['speed-to-lead', 'pools'] });
  };
  const save = useMutation({
    mutationFn: (f: PoolForm) => {
      const body: PoolBody = { name: f.name, brandAccountId: f.brandAccountId, clubAccountIds: f.clubAccountIds, numbers: f.numbers, groupStaff: f.groupStaff.filter((s) => s.mobile.trim() !== ''), primaryNumber: f.primaryNumber.trim() === '' ? null : f.primaryNumber.trim() };
      return f.id ? updatePool(f.id, body) : createPool(body);
    },
    onSuccess: () => { toast.success('Pool saved.'); done(); },
    onError: (err) => toast.error(errMessage(err)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => deletePool(id),
    onSuccess: () => { toast.success('Pool deleted.'); done(); },
    onError: (err) => toast.error(errMessage(err)),
  });

  return (
    <Section title="Number pools" description={`A pool is up to ${MAX_CLUBS_PER_POOL} clubs of one brand sharing Vonage numbers. A dedicated number is a pool of one club. A club is in one pool, a number serves one pool.`}>
      {pools.isLoading && <CenteredSpinner />}
      {pools.error && <p className="text-sm text-red-600">{errMessage(pools.error)}</p>}
      <ul className="space-y-2">
        {pools.data?.pools.map((p) => (
          <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-200 p-3 text-sm">
            <div>
              <p className="font-medium text-slate-900">{p.name}</p>
              <p className="text-slate-500">{p.clubAccountIds.length} club{p.clubAccountIds.length === 1 ? '' : 's'} · {p.numbers.join(', ')}</p>
            </div>
            {isAdmin && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setForm({ id: p.id, name: p.name, brandAccountId: p.brandAccountId, clubAccountIds: p.clubAccountIds, numbers: p.numbers, groupStaff: p.groupStaff.map((s) => ({ ...s })), primaryNumber: p.primaryNumber ?? '' })}>Edit</Button>
                <Button variant="outline" size="sm" disabled={remove.isPending} onClick={() => { if (window.confirm(`Delete the pool "${p.name}"? Conversations keep the number they started on.`)) remove.mutate(p.id); }}>Delete</Button>
              </div>
            )}
          </li>
        ))}
        {pools.data && pools.data.pools.length === 0 && <li className="text-sm text-slate-500">No pools yet.</li>}
      </ul>

      {isAdmin && !form && <Button className="mt-4" variant="outline" onClick={() => setForm(EMPTY_POOL)}>New pool</Button>}

      {form && (
        <div className="mt-5 space-y-4 rounded-md border border-slate-200 p-4">
          <label className="block max-w-md text-sm text-slate-700"><span className="mb-1 block">Pool name</span>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} aria-label="Pool name" />
          </label>
          <div className="max-w-md text-sm text-slate-700"><span className="mb-1 block">Brand</span>
            <Combobox value={form.brandAccountId} onValueChange={(v) => setForm({ ...form, brandAccountId: v })} options={brands} placeholder="Choose the brand" aria-label="Brand" />
          </div>
          <div className="text-sm text-slate-700">
            <span className="mb-1 block">Clubs ({form.clubAccountIds.length} of {MAX_CLUBS_PER_POOL})</span>
            <div className="mb-2 flex flex-wrap gap-2">
              {form.clubAccountIds.map((id) => (
                <button key={id} type="button" className="rounded-full bg-slate-100 px-3 py-1 text-xs" onClick={() => setForm({ ...form, clubAccountIds: form.clubAccountIds.filter((c) => c !== id) })} aria-label={`Remove ${nameOf.get(id) ?? id}`}>
                  {nameOf.get(id) ?? id} ×
                </button>
              ))}
            </div>
            <div className="max-w-md">
              <Combobox value="" onValueChange={(v) => v && setForm({ ...form, clubAccountIds: [...form.clubAccountIds, v] })} options={clubOptions} placeholder="Add a club" searchPlaceholder="Search clubs" aria-label="Add a club" />
            </div>
          </div>
          <div className="text-sm text-slate-700">
            <span className="mb-1 block">Numbers (registered Vonage numbers only)</span>
            {pools.data?.numbers.length === 0 && <p className="text-xs text-slate-500">No eligible numbers are registered yet.</p>}
            {pools.data?.numbers.map((n) => (
              <label key={n.e164} className="mr-4 inline-flex items-center gap-2">
                <input type="checkbox" className="h-4 w-4" checked={form.numbers.includes(n.e164)} onChange={(e) => setForm({ ...form, numbers: e.target.checked ? [...form.numbers, n.e164] : form.numbers.filter((x) => x !== n.e164) })} />
                {n.e164}
                {n.warning && <span role="alert" className="ml-1 text-xs text-amber-700">{n.warning}</span>}
              </label>
            ))}
          </div>
          <div className="text-sm text-slate-700">
            <span className="mb-1 block">Group staff (told when nobody can say which club a texter means)</span>
            {form.groupStaff.map((s, i) => (
              <div key={i} className="mb-2 flex gap-2">
                <Input className="w-40" placeholder="Name" value={s.name} onChange={(e) => setForm({ ...form, groupStaff: form.groupStaff.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} aria-label="Group staff name" />
                <Input className="w-44" placeholder="Mobile" value={s.mobile} onChange={(e) => setForm({ ...form, groupStaff: form.groupStaff.map((x, j) => (j === i ? { ...x, mobile: e.target.value } : x)) })} aria-label="Group staff mobile" />
                <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, groupStaff: form.groupStaff.filter((_, j) => j !== i) })}>Remove</Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, groupStaff: [...form.groupStaff, { name: '', mobile: '' }] })}>Add person</Button>
          </div>
          <label className="block max-w-xs text-sm text-slate-700">
            <span className="mb-1 block">Primary number for unknown callers (inbound calls)</span>
            <Input value={form.primaryNumber} placeholder="leave empty to switch off" onChange={(e) => setForm({ ...form, primaryNumber: e.target.value })} aria-label="Pool primary number" />
            <span className="text-xs text-slate-400">
              When someone calls this line and we cannot tell which club they want, ring this number, then the group staff above. Empty means
              they hear a short message instead. Calls are never recorded.
            </span>
          </label>
          <div className="flex gap-2">
            <Button onClick={() => save.mutate(form)} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save pool'}</Button>
            <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
          </div>
        </div>
      )}
    </Section>
  );
}

